'use client'

// React Imports
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

// Dictionary Imports
import { dictionaries, isLang, LOCALES } from './dictionaries'
import type { Dictionary, Lang } from './dictionaries'

type LanguageContextValue = {
  lang: Lang
  setLang: (lang: Lang) => void
  dictionary: Dictionary
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

const STORAGE_KEY = 'tvstor-lang'

// The cookie mirrors the choice so the Next SERVER already renders the right
// language — no more english flash before hydration (Luca 2026-07-31).
// localStorage stays for the cross-tab `storage` event only (cookies fire none).
export const LANG_COOKIE = 'tvstor-lang'

const persist = (next: Lang) => {
  localStorage.setItem(STORAGE_KEY, next)
  document.cookie = `${LANG_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`
}

export const LanguageProvider = ({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) => {
  // initialLang comes from the cookie read server-side (Providers): SSR and
  // first client render agree on the RIGHT language, not on 'en'.
  const [lang, setLangState] = useState<Lang>(initialLang ?? 'en')

  useEffect(() => {
    // localStorage resta SOVRANO al mount: se diverge dal cookie (es. cookie
    // scritto dal rilevamento su un'altra pagina, o scelta fatta quando il
    // cookie non esisteva — il caso che ha steso 64/66 e2e) vince lui e il
    // cookie si riallinea. Senza nessuno dei due si rileva dal browser e si
    // scrive il cookie per il prossimo SSR.
    const stored = localStorage.getItem(STORAGE_KEY)

    if (isLang(stored)) {
      setLangState(stored)
      persist(stored)
    } else if (initialLang === undefined) {
      // First supported locale the browser language starts with ('pt-BR' → pt,
      // 'de-AT' → de); English stays the fallback for everything else.
      const navLang = navigator.language.toLowerCase()
      const detected: Lang = LOCALES.find(code => navLang.startsWith(code)) ?? 'en'

      setLangState(detected)
      persist(detected)
    }

    // Cross-tab sync (Luca 2026-07-24): the `storage` event fires in every
    // OTHER tab when one writes the key — the language change applies live
    // everywhere instead of waiting for a reload.
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && isLang(event.newValue)) {
        setLangState(event.newValue)
        document.cookie = `${LANG_COOKIE}=${event.newValue}; path=/; max-age=31536000; samesite=lax`
      }
    }

    window.addEventListener('storage', onStorage)

    return () => window.removeEventListener('storage', onStorage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      setLang: (next: Lang) => {
        persist(next)
        setLangState(next)
      },
      dictionary: dictionaries[lang]
    }),
    [lang]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useTranslation = () => {
  const context = useContext(LanguageContext)

  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider')
  }

  const t = (key: string): string => {
    const value = key.split('.').reduce<unknown>((node, part) => {
      return node && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined
    }, context.dictionary)

    return typeof value === 'string' ? value : key
  }

  return { t, lang: context.lang, setLang: context.setLang }
}
